import type { IColor } from '@color';
import { ObjectFit, type ImageSource } from '@image';
import type { IString } from '@strings';
import type { LayoutTextOptions, MeasuredText } from '@typography';
import {
  FixedFontSizeChooser,
  ScalingFontSizeChooser,
  type FontSizeChooser
} from '@typography';
import { z } from 'zod';
import { BorderElementConfig } from './BorderElementConfig';
import { ElementDecl } from './ElementDecl';
import { FontSizeChooserSchema, SpecSchema } from './schema';
import { SnorfSpec } from './SnorfSpec';
import type { CornerRadius, ElementId, ImageElementData, TextElementData } from './types';
import {
  ChildAlignment,
  LayoutAlignmentX,
  LayoutAlignmentY,
  LayoutConfig,
  LayoutDirection,
  Sizing,
  SizingAxis,
  SizingType
} from './types';

// Define the recursive element interface that matches the data AFTER SpecSchema.parse()
// Note: IColor fields will be actual IColor objects, not JSON arrays, due to schema transformation
interface ValidatedElement {
  id?: ElementId;
  layout: {
    sizing: {
      width: { type: SizingType; size: number | { min: number; max: number } };
      height: { type: SizingType; size: number | { min: number; max: number } };
    };
    padding: { leading: number; trailing: number; top: number; bottom: number };
    childGap: number;
    direction: LayoutDirection;
    childAlignment: { x: LayoutAlignmentX; y: LayoutAlignmentY };
  };
  backgroundColor?: IColor;
  cornerRadius?: {
    topLeft: number;
    topRight: number;
    bottomLeft: number;
    bottomRight: number;
  };
  border?: {
    color?: IColor;
    width:
      | number
      | { left: number; right: number; top: number; bottom: number }
      | { betweenChildren: number };
  };
  textData?: {
    text: IString;
    color: IColor;
    layoutOptions?: {
      fontSizeChooser?: z.infer<typeof FontSizeChooserSchema>;
      minWidth?: number;
      maxWidth?: number;
      aspectRatio?: number;
      maxLines?: number;
      basicTextStyle?: string;
    };
    measured?: MeasuredText;
  };
  imageData?: {
    source: ImageSource;
    objectFit: ObjectFit;
    opacity: number;
  };
  // Visual enhancement properties
  hovered?: boolean;
  isSelected?: boolean;
  hasCopyOverlay?: boolean;
  opacity?: number;
  clip?: boolean;
  zIndex?: number;
  children?: ValidatedElement[];
}

/**
 * Transform utilities for converting Zod-parsed data to SNORF classes
 */
export const Snorf = {
  /**
   * Create a spec from JSON data using Zod validation
   */
  fromJSON(data: unknown): SnorfSpec {
    const parsed = SpecSchema.parse(data);
    const spec = new SnorfSpec();

    // Transform elements
    for (const elementData of parsed.elements) {
      const element = transformElementDecl(elementData, spec);
      spec.addElement(element);
    }

    return spec;
  },

  /**
   * Convert a SnorfSpec instance back to schema-compatible JSON
   */
  toJSON(spec: SnorfSpec): Record<string, unknown> {
    // Find root elements (those without parents)
    const rootElements = Array.from(spec.allElements).filter(e => e.isRoot);

    // Convert each root element and its children to JSON
    const elements = rootElements.map(element => elementDeclToJSON(spec, element));

    return {
      elements
    };
  }
};

/**
 * Convert an ElementDecl instance to schema-compatible JSON
 */
function elementDeclToJSON(
  spec: SnorfSpec,
  element: ElementDecl
): Record<string, unknown> {
  const result: Record<string, unknown> = {
    id: element.id as string
  };

  // Always use flat format (no layout wrapper) since our simplified schema only supports this
  const widthJson = sizingAxisToJSONWithAlias(element.layoutConfig.sizing.width);
  const heightJson = sizingAxisToJSONWithAlias(element.layoutConfig.sizing.height);

  // Only include width if it's not the default
  if (!isSizingAxisDefault(element.layoutConfig.sizing.width)) {
    result.width = widthJson;
  }

  // Only include height if it's not the default
  if (!isSizingAxisDefault(element.layoutConfig.sizing.height)) {
    result.height = heightJson;
  }

  // Add other layout properties directly at the element level if they differ from defaults
  if (!isPaddingDefault(element.layoutConfig.padding)) {
    result.padding = paddingToJSON(element.layoutConfig.padding);
  }

  if (element.layoutConfig.childGap !== 0) {
    result.childGap = element.layoutConfig.childGap;
  }

  if (element.layoutConfig.direction !== LayoutDirection.LEFT_TO_RIGHT) {
    result.direction = element.layoutConfig.direction;
  }

  // Build childAlignment object, suppressing default values
  const childAlignment: Record<string, unknown> = {};

  // Only include x alignment if it's not the default
  if (element.layoutConfig.childAlignment.x !== LayoutAlignmentX.LEFT) {
    childAlignment.x = element.layoutConfig.childAlignment.x;
  }

  // Only include y alignment if it's not the default
  if (element.layoutConfig.childAlignment.y !== LayoutAlignmentY.TOP) {
    childAlignment.y = element.layoutConfig.childAlignment.y;
  }

  // Only add childAlignment if at least one axis is non-default
  if (Object.keys(childAlignment).length > 0) {
    result.childAlignment = childAlignment;
  }

  // Add optional properties only if they exist and are not defaults
  if (element.backgroundColor) {
    result.backgroundColor = element.backgroundColor.toJSON();
  }

  if (hasNonZeroCornerRadius(element.cornerRadius)) {
    result.cornerRadius = cornerRadiusToJSON(element.cornerRadius);
  }

  if (hasBorder(element.border)) {
    result.border = borderToJSON(element.border);
  }

  if (element.textData) {
    result.textData = textElementDataToJSON(element.textData);
  }

  if (element.imageData) {
    result.imageData = imageElementDataToJSON(element.imageData);
  }

  // Add visual enhancement properties if they differ from defaults
  if (element.hovered) {
    result.hovered = element.hovered;
  }
  if (element.isSelected) {
    result.isSelected = element.isSelected;
  }
  if (element.hasCopyOverlay) {
    result.hasCopyOverlay = element.hasCopyOverlay;
  }
  if (element.opacity !== 1) {
    result.opacity = element.opacity;
  }
  if (element.zIndex !== 0) {
    result.zIndex = element.zIndex;
  }
  if (element.clip) {
    result.clip = element.clip;
  }

  // Add children recursively
  if (element.childIds.length > 0) {
    result.children = spec
      .getChildren(element)
      .map(child => elementDeclToJSON(spec, child));
  }

  return result;
}

/**
 * Convert Sizing to JSON format, using aliases where possible
 */

/**
 * Check if padding matches default values
 */
function isPaddingDefault(padding: {
  leading: number;
  trailing: number;
  top: number;
  bottom: number;
}): boolean {
  return (
    padding.leading === 0 &&
    padding.trailing === 0 &&
    padding.top === 0 &&
    padding.bottom === 0
  );
}

/**
 * Check if sizing axis is the default "fit" type
 */
function isSizingAxisDefault(axis: SizingAxis): boolean {
  return (
    axis.type === SizingType.FIT &&
    typeof axis.size === 'object' &&
    axis.size.min === 0 &&
    axis.size.max === Number.MAX_VALUE
  );
}

/**
 * Convert a SizingAxis to JSON format
 */
function sizingAxisToJSON(axis: SizingAxis): Record<string, unknown> {
  const result: Record<string, unknown> = {
    type: axis.type
  };

  if (typeof axis.size === 'number') {
    // For percent type, size is a number
    result.size = axis.size;
  } else {
    // For min/max size object, omit default values
    const sizeObj: Record<string, unknown> = {};
    if (axis.size.min !== 0) {
      sizeObj.min = axis.size.min;
    }
    if (axis.size.max !== Number.MAX_VALUE) {
      sizeObj.max = axis.size.max;
    }

    // Only include size if it has any non-default values
    if (Object.keys(sizeObj).length > 0) {
      result.size = sizeObj;
    }
  }

  return result;
}

/**
 * Check if corner radius has any non-zero values
 */
function hasNonZeroCornerRadius(cornerRadius: CornerRadius): boolean {
  return (
    cornerRadius.topLeft > 0 ||
    cornerRadius.topRight > 0 ||
    cornerRadius.bottomLeft > 0 ||
    cornerRadius.bottomRight > 0
  );
}

/**
 * Check if border has any non-default configuration
 */
function hasBorder(border: BorderElementConfig): boolean {
  // Check if color is set (default is undefined/no color)
  if (border.color) return true;

  // Check if width is non-default
  const width = border.width;
  if ('betweenChildren' in width) {
    return width.betweenChildren > 0;
  } else {
    return width.left > 0 || width.right > 0 || width.top > 0 || width.bottom > 0;
  }
}

/**
 * Convert BorderElementConfig to JSON format, omitting default values and using aliases where possible
 */
function borderToJSON(border: BorderElementConfig): number | Record<string, unknown> {
  // Check for top-level alias: no color + uniform width
  const width = border.width;
  if (!border.color && 'left' in width) {
    const w = width as {
      left: number;
      right: number;
      top: number;
      bottom: number;
    };
    if (w.left === w.right && w.left === w.top && w.left === w.bottom && w.left > 0) {
      return w.left; // Use top-level alias: border: number
    }
  }

  // Use explicit form
  const result: Record<string, unknown> = {};

  if (border.color) {
    result.color = border.color.toJSON();
  }

  // Only include width if it's not the default (all zeros), using aliases where possible
  if ('betweenChildren' in width) {
    if (width.betweenChildren > 0) {
      result.width = width;
    }
  } else {
    if (width.left > 0 || width.right > 0 || width.top > 0 || width.bottom > 0) {
      result.width = borderWidthToJSON(width);
    }
  }

  return result;
}

/**
 * Convert TextElementData to JSON format, omitting default values
 */
function textElementDataToJSON(textData: TextElementData): Record<string, unknown> {
  const result: Record<string, unknown> = {
    text: textData.text.toJSON(),
    color: textData.color.toJSON()
  };

  // style round-trip occurs via layoutOptions.basicTextStyle

  if (
    Object.keys(textData.layoutOptions).length > 0 ||
    textData.layoutOptions.basicTextStyle !== undefined
  ) {
    const layoutOptions: Record<string, unknown> = {};

    if (textData.layoutOptions.fontSizeChooser) {
      layoutOptions.fontSizeChooser = fontSizeChooserToJSON(
        textData.layoutOptions.fontSizeChooser
      );
    }

    if (textData.layoutOptions.minWidth !== undefined) {
      layoutOptions.minWidth = textData.layoutOptions.minWidth;
    }
    if (textData.layoutOptions.maxWidth !== undefined) {
      layoutOptions.maxWidth = textData.layoutOptions.maxWidth;
    }
    if (textData.layoutOptions.aspectRatio !== undefined) {
      layoutOptions.aspectRatio = textData.layoutOptions.aspectRatio;
    }
    if (textData.layoutOptions.maxLines !== undefined) {
      layoutOptions.maxLines = textData.layoutOptions.maxLines;
    }
    if (textData.layoutOptions.basicTextStyle !== undefined) {
      // Emit alias 'style' for user-facing JSON
      layoutOptions.style = textData.layoutOptions.basicTextStyle;
    }

    // Only include layoutOptions if there are any non-default options
    if (Object.keys(layoutOptions).length > 0) {
      result.layoutOptions = layoutOptions;
    }
  }

  return result;
}

/**
 * Convert ImageElementData to JSON format
 */
function imageElementDataToJSON(imageData: ImageElementData): Record<string, unknown> {
  return {
    source: imageData.source, // TODO: Implement proper ImageSource serialization
    objectFit: imageData.objectFit,
    opacity: imageData.opacity
  };
}

/**
 * Convert FontSizeChooser to JSON format
 */
function fontSizeChooserToJSON(chooser: FontSizeChooser): Record<string, unknown> {
  if (chooser instanceof FixedFontSizeChooser) {
    return {
      type: 'fixed',
      fontSize: chooser.fontSizeValue
    };
  }

  if (chooser instanceof ScalingFontSizeChooser) {
    return {
      type: 'scaling',
      fontSizeRange: chooser.fontSizeRangeValue,
      taperCharCountRange: chooser.taperCharCountRangeValue
    };
  }

  throw new Error(`Unknown FontSizeChooser type: ${chooser.constructor.name}`);
}

/**
 * Transform a single ElementDecl from JSON data
 */
function transformElementDecl(data: unknown, spec: SnorfSpec): ElementDecl {
  // The data has already been validated by SpecSchema.parse() in fromJSON()
  // so we can safely cast it to our expected type
  const validatedData = data as ValidatedElement;

  const element = new ElementDecl();

  // Set ID - if provided, use it; otherwise constructor already generated a random one
  if (validatedData.id) {
    element.id = validatedData.id as unknown as ElementId;
  }

  // Transform layout config
  element.layoutConfig = transformLayoutConfig(validatedData.layout);

  // Set other properties
  if (validatedData.backgroundColor) {
    element.backgroundColor = validatedData.backgroundColor;
  }

  element.cornerRadius = validatedData.cornerRadius ?? {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  };
  element.border = transformBorderConfig(validatedData.border);

  if (validatedData.textData) {
    const transformedText = transformTextElementData(validatedData.textData);
    element.textData = transformedText;
  }

  if (validatedData.imageData) {
    const transformedImage = transformImageElementData(validatedData.imageData);
    if (transformedImage) {
      element.imageData = transformedImage;
    }
  }

  // Set visual enhancement properties
  if (validatedData.hovered !== undefined) {
    element.hovered = validatedData.hovered;
  }
  if (validatedData.isSelected) {
    element.isSelected = validatedData.isSelected;
  }
  if (validatedData.hasCopyOverlay !== undefined) {
    element.hasCopyOverlay = validatedData.hasCopyOverlay;
  }
  if (validatedData.opacity !== undefined) {
    element.opacity = validatedData.opacity;
  }
  if (validatedData.zIndex !== undefined) {
    element.zIndex = validatedData.zIndex;
  }
  if (validatedData.clip !== undefined) {
    element.clip = validatedData.clip;
  }

  // Transform children if present
  if (validatedData.children) {
    for (const childData of validatedData.children) {
      const childElement = transformElementDecl(childData, spec);
      element.addChild(childElement.id);
      childElement.parentId = element.id;
      spec.addElement(childElement);
    }
  }

  return element;
}

/**
 * Transform LayoutConfig from JSON data
 */
function transformLayoutConfig(data: ValidatedElement['layout']): LayoutConfig {
  const config = new LayoutConfig();

  // Transform sizing
  config.sizing = transformSizing(data.sizing);

  // Set other properties
  config.padding = data.padding;
  config.childGap = data.childGap;
  config.direction = data.direction;
  config.childAlignment = transformChildAlignment(data.childAlignment);

  return config;
}

/**
 * Transform Sizing from JSON data
 */
function transformSizing(data: ValidatedElement['layout']['sizing']): Sizing {
  const sizing = new Sizing();

  sizing.width = transformSizingAxis(data.width);
  sizing.height = transformSizingAxis(data.height);

  return sizing;
}

/**
 * Transform SizingAxis from JSON data
 */
function transformSizingAxis(
  data: ValidatedElement['layout']['sizing']['width']
): SizingAxis {
  const axis = new SizingAxis();

  axis.type = data.type;
  axis.size = data.size;

  return axis;
}

/**
 * Transform ChildAlignment from JSON data
 */
function transformChildAlignment(
  data: ValidatedElement['layout']['childAlignment']
): ChildAlignment {
  const alignment = new ChildAlignment();

  alignment.x = data.x;
  alignment.y = data.y;

  return alignment;
}

/**
 * Transform BorderElementConfig from JSON data
 */
function transformBorderConfig(data?: ValidatedElement['border']): BorderElementConfig {
  const border = new BorderElementConfig();

  if (data?.color) {
    border.color = data.color;
  }

  if (data?.width) {
    border.width = data.width as BorderElementConfig['width'];
  } else {
    border.width = { left: 0, right: 0, top: 0, bottom: 0 };
  }

  return border;
}

/**
 * Transform FontSizeChooser from JSON data
 */
function transformFontSizeChooser(
  data: z.infer<typeof FontSizeChooserSchema>
): FontSizeChooser {
  if (data.type === 'fixed') {
    return new FixedFontSizeChooser(data.fontSize);
  } else {
    // data.type === 'scaling' - this is the only other valid type from schema
    return new ScalingFontSizeChooser(data.fontSizeRange, data.taperCharCountRange);
  }
}

/**
 * Transform TextElementData from JSON data
 */
function transformTextElementData(
  data: ValidatedElement['textData']
): TextElementData | undefined {
  if (!data) return undefined;

  const result: TextElementData = {
    text: data.text,
    color: data.color,
    layoutOptions: {}
  };

  // no separate style; use layoutOptions.basicTextStyle

  if (data.layoutOptions) {
    // Start with empty options and fill in what we have
    const layoutOptions: Partial<LayoutTextOptions> = {};

    // Transform FontSizeChooser if present
    if (data.layoutOptions.fontSizeChooser) {
      layoutOptions.fontSizeChooser = transformFontSizeChooser(
        data.layoutOptions.fontSizeChooser as unknown as z.infer<
          typeof FontSizeChooserSchema
        >
      );
    }

    // Copy other properties
    if (data.layoutOptions.minWidth !== undefined) {
      layoutOptions.minWidth = data.layoutOptions.minWidth;
    }
    if (data.layoutOptions.maxWidth !== undefined) {
      layoutOptions.maxWidth = data.layoutOptions.maxWidth;
    }
    if (data.layoutOptions.aspectRatio !== undefined) {
      layoutOptions.aspectRatio = data.layoutOptions.aspectRatio;
    }
    if (data.layoutOptions.maxLines !== undefined) {
      layoutOptions.maxLines = data.layoutOptions.maxLines;
    }
    if (data.layoutOptions.basicTextStyle !== undefined) {
      layoutOptions.basicTextStyle = data.layoutOptions
        .basicTextStyle as unknown as LayoutTextOptions['basicTextStyle'];
    }

    result.layoutOptions = layoutOptions;
  }

  if (data.measured) {
    // Type assertion is safe here because the data came from Zod validation
    // TODO: Replace with proper MeasuredText schema when implemented
    result.measured = data.measured;
  }

  return result;
}

/**
 * Transform ImageElementData from JSON data
 */
function transformImageElementData(
  data: ValidatedElement['imageData']
): ImageElementData | undefined {
  if (!data) return undefined;

  return {
    source: data.source,
    objectFit: data.objectFit,
    opacity: data.opacity
  };
}

/**
 * Convert a SizingAxis to JSON format, using alias if it's fixed with min==max
 */
function sizingAxisToJSONWithAlias(
  axis: SizingAxis
): string | number | Record<string, unknown> {
  // Check if it's fit or grow type with default sizing (can use string alias)
  if (
    (axis.type === SizingType.FIT || axis.type === SizingType.GROW) &&
    typeof axis.size === 'object' &&
    axis.size.min === 0 &&
    axis.size.max === Number.MAX_VALUE
  ) {
    return axis.type; // Use string alias: 'fit' or 'grow'
  }

  // Check if it's fixed type with min==max (can use number alias)
  if (
    axis.type === SizingType.FIXED &&
    typeof axis.size === 'object' &&
    axis.size.min === axis.size.max
  ) {
    return axis.size.min; // Use number alias
  }

  // Use explicit form
  return sizingAxisToJSON(axis);
}

/**
 * Convert padding to JSON format, using alias if all sides are equal
 */
function paddingToJSON(padding: {
  leading: number;
  trailing: number;
  top: number;
  bottom: number;
}): number | Record<string, unknown> {
  // Check if all sides are equal (can use alias)
  if (
    padding.leading === padding.trailing &&
    padding.leading === padding.top &&
    padding.leading === padding.bottom
  ) {
    return padding.leading; // Use alias form: number
  }

  // Use explicit form, but omit default zero values
  const result: Record<string, unknown> = {};
  if (padding.leading !== 0) result.leading = padding.leading;
  if (padding.trailing !== 0) result.trailing = padding.trailing;
  if (padding.top !== 0) result.top = padding.top;
  if (padding.bottom !== 0) result.bottom = padding.bottom;

  return result;
}

/**
 * Convert corner radius to JSON format, using alias if all corners are equal
 */
function cornerRadiusToJSON(cornerRadius: CornerRadius): number | CornerRadius {
  // Check if all corners are equal (can use alias)
  if (
    cornerRadius.topLeft === cornerRadius.topRight &&
    cornerRadius.topLeft === cornerRadius.bottomLeft &&
    cornerRadius.topLeft === cornerRadius.bottomRight
  ) {
    return cornerRadius.topLeft; // Use alias form: number
  }

  // Use explicit form
  return cornerRadius;
}

/**
 * Convert border width to JSON format, using alias if all sides are equal
 */
function borderWidthToJSON(width: {
  left: number;
  right: number;
  top: number;
  bottom: number;
}): number | { left: number; right: number; top: number; bottom: number } {
  // Check if all sides are equal (can use alias)
  if (
    width.left === width.right &&
    width.left === width.top &&
    width.left === width.bottom
  ) {
    return width.left; // Use alias form: number
  }

  // Use explicit form
  return width;
}
